import { match } from 'ts-pattern';
import {
  BusinessStatus,
  IPickUpWhreYouLeft,
  OnboardingStatus,
  OnboardingTypeEnum,
} from '@Store/slices/customer/customer.types.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { router } from '@Routes/__root.tsx';
import { routesModel } from '@Router/router.consts.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { GenericMessageTypes } from '@Pages/genericMessagePage/queryParamsSchema.ts';
import { NAVIGATION_STACK_VERSION } from '@Types/consts.ts';
import { reArrangeNavigationStack } from '@Services/init/util.ts';

interface IPickUpWhereLeftFunction {
  onboardingStatus?: OnboardingStatus;
  onboardingTypeEnum?: OnboardingTypeEnum;
  lastStepData?: IPickUpWhreYouLeft;
}

export const handleLoginResponse = ({
  onboardingStatus,
  onboardingTypeEnum,
  lastStepData,
}: IPickUpWhereLeftFunction) => {
  const { getCustomerData } = useBoundStore.getState();
  const { navigationStack } = useBoundStore.getState().onboardingSlice.getOnboardingData();
  const { business } = getCustomerData();

  const businessStatus = business?.status || undefined;

  const navigateToFirstPage = () => {
    router.navigate({ to: routesModel[navigationStack[0]].path });
  };
  if (!onboardingStatus) {
    navigateToFirstPage();
    return;
  }
  const navigateToSpecificPage = (page: RoutesMap, searchParam?: object) => {
    router.navigate({ to: routesModel[page].path, search: searchParam });
  };

  const lastStepContinueWhereYouLeft = async (stepData: IPickUpWhreYouLeft) => {
    const { version, stepName } = stepData;
    if (version < NAVIGATION_STACK_VERSION) {
      navigateToFirstPage();
      return;
    }

    await reArrangeNavigationStack(stepName);
  };

  match({
    businessStatus,
    onboardingStatus,
    onboardingTypeEnum,
    lastStepData,
  })
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.MULTI_BUSINESS,
        lastStepData: undefined,
      },
      () => {
        navigateToSpecificPage(RoutesMap.BUSINESS_TYPE_SELECTION);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.MULTI_BUSINESS,
        lastStepData: lastStepData,
      },
      () => {
        lastStepContinueWhereYouLeft(lastStepData!);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.NO_CONTINUE_BLOCK,
      },
      () => {
        navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
          type: GenericMessageTypes.KYC_FAILED,
        });
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        lastStepData: undefined,
      },
      () => {
        navigateToFirstPage();
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.STANDARD,
        lastStepData: lastStepData,
      },
      () => {
        lastStepContinueWhereYouLeft(lastStepData!);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.NEW,
      },
      () => {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.RESUBMIT,
      },
      () => {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.PENDING,
      },
      () => {
        navigateToSpecificPage(RoutesMap.VERIFICATION_CENTER);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.KYC_PENDING,
      },
      () => {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: undefined,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYB_PENDING);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.NONE,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYB_PENDING);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.COMPLETED,
      },
      () => {
        navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
          type: GenericMessageTypes.ONE_OF_US,
        });
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYC_FAILED_UPLOADED_DOCS,
      },
      () => {
        navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
          type: GenericMessageTypes.HANG_TIGHT,
        });
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYC_FIRST_FAILED,
      },
      () => {
        navigateToSpecificPage(RoutesMap.SOCURE_DOC_V);
      }
    )
    .otherwise(() => {
      navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
        type: GenericMessageTypes.KYC_FAILED,
      });
    });
};
